import { oc } from "@orpc/contract";
import { z } from "zod";
import { EDI849ResponseStatusSchema } from "./edi849-responses.contract";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

/**
 * EDI844 Submission Status enum (matching backend model)
 */
export const EDI844SubmissionStatusSchema = z.enum([
  "draft",
  "sent",
  "pending",
  "pending_vdm",
  "sending",
  "vdm_failed",
  "email_failed",
  "failed",
  "as400_confirmed",
  "disputed",
  "accepted",
  "rejected",
  "partial",
]);

export const EDI844_SUBMISSION_STATUS = EDI844SubmissionStatusSchema.enum;

/**
 * EDI844 Delivery Method enum (matching backend model)
 */
export const EDI844DeliveryMethodSchema = z.enum(["edi", "email"]);

export const EDI844_DELIVERY_METHOD = EDI844DeliveryMethodSchema.enum;

export type EDI844SubmissionStatusValue = z.infer<
  typeof EDI844SubmissionStatusSchema
>;
export type EDI844DeliveryMethodValue = z.infer<
  typeof EDI844DeliveryMethodSchema
>;

/**
 * Human-readable submission status labels. The same status reads differently
 * depending on how the claim was delivered (an EDI claim goes to AS400, an
 * email claim goes straight to the supplier), so there is one map per delivery
 * method.
 *
 * These live in the contract because both the 844 submissions page (row badges,
 * status filter) and the backend Excel export render them — keeping one copy
 * stops the exported spreadsheet from disagreeing with the screen it came from.
 */
export const EDI844_EDI_STATUS_LABELS: Record<
  EDI844SubmissionStatusValue,
  string
> = {
  draft: "Draft",
  pending: "Processing",
  pending_vdm: "Awaiting VDM Confirmation",
  sending: "Sending Email",
  vdm_failed: "VDM Failed",
  email_failed: "Email Failed",
  failed: "Failed",
  sent: "Sent to AS400",
  as400_confirmed: "Awaiting EDI",
  accepted: "Accepted",
  partial: "Partially Accepted",
  disputed: "Partially Accepted",
  rejected: "Rejected",
};

export const EDI844_EMAIL_STATUS_LABELS: Record<
  EDI844SubmissionStatusValue,
  string
> = {
  ...EDI844_EDI_STATUS_LABELS,
  pending: "Submission Created",
  sent: "Sent to Supplier",
};

export function getEDI844StatusLabel(
  status: EDI844SubmissionStatusValue,
  deliveryMethod?: EDI844DeliveryMethodValue,
): string {
  const labels =
    deliveryMethod === EDI844_DELIVERY_METHOD.email
      ? EDI844_EMAIL_STATUS_LABELS
      : EDI844_EDI_STATUS_LABELS;
  return labels[status] ?? status;
}

/**
 * EDI844 Submission Line Status enum (matching backend model)
 */
export const EDI844SubmissionLineStatusSchema = z.enum([
  "pending",
  "marked_failed",
  "edited_for_resubmission",
  "resubmitted",
  "accepted",
  "rejected",
  "partial",
  "disputed",
]);

/**
 * EDI844 Submission Activity Type enum (matching backend model)
 */
export const EDI844SubmissionActivityTypeSchema = z.enum([
  "created",
  "sent_via_edi",
  "sent_via_email",
  "as400_confirmed",
  "response_received",
  "response_849_processed",
  "line_accepted",
  "line_rejected",
  "line_accepted_849",
  "line_rejected_849",
  "line_partial",
  "disputed",
  "dispute_response",
  "comment_added",
  "status_changed",
  "vdm_created",
  "error_occurred",
  "vdm_linked",
  "vdm_unlinked",
  "marked_failed",
  "resubmitted",
]);

/**
 * Pagination schema
 */
export const EDI844PaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Company summary for submission response
 */
const SubmissionCompanySchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * User summary for activity response
 */
const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * Source transaction summary (from RexelE844sd)
 */
const SourceTransactionSchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable().optional(),
  customerPoNumber: z.string().nullable().optional(),
  itemInternalCode: z.string().nullable().optional(),
  shippedQuantity: z.coerce.number().nullable().optional(),
  claimAmount: z.coerce.number().nullable().optional(),
  tradingPartner: z.string().nullable().optional(),
  agreementNumber: z.string().nullable().optional(),
  salesOrderLineNumber: z.coerce.number().nullable().optional(),
  sellingBranchName: z.string().nullable().optional(),
  invoiceDate: z.string().nullable().optional(),
  orderDate: z.string().nullable().optional(),
});

/**
 * EDI844 Submission Line schema
 */
export const EDI844SubmissionLineSchema = z.object({
  id: UUIDSchema,
  submissionId: UUIDSchema,
  rexelE844sdId: UUIDSchema,
  lineStatus: EDI844SubmissionLineStatusSchema,
  claimedAmount: DecimalStringSchema,
  acceptedAmount: DecimalStringSchema.nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  batchControlNumber: z.string().nullable().optional(),
  responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  sourceTransaction: SourceTransactionSchema.optional(),
  sdE844dId: UUIDSchema.nullable().optional(),
  // Per-line VDM linkage (sd_vdm_transactions → sd_vdms), with the legacy
  // submission-level vdm_id as a fallback for un-backfilled older submissions.
  vdmId: UUIDSchema.nullable().optional(),
  vdmNumber: z.number().nullable().optional(),
  gentlyVdmNumber: z.number().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Response status indicating whether 849 responses have been received
 */
export const EDI844ResponseStatusSchema = z.enum([
  "none",
  "partial",
  "complete",
]);
export type EDI844ResponseStatus = z.infer<typeof EDI844ResponseStatusSchema>;

/**
 * VDM summary for submission response
 */
const SubmissionVDMSchema = z.object({
  id: UUIDSchema,
  gentlyVdmNumber: z.int(),
  vdmNumber: z.int().nullable().optional(),
  status: z.string(),
});

/**
 * Linked 849 response summary
 */
const LinkedResponseSummarySchema = z.object({
  id: UUIDSchema,
  supplierName: z.string(),
  status: EDI849ResponseStatusSchema,
  acceptedCount: z.int().nonnegative(),
  rejectedCount: z.int().nonnegative(),
  partialCount: z.int().nonnegative(),
  processedAt: z.coerce.date(),
});

/**
 * EDI844 Submission schema for RPC responses
 */
export const EDI844SubmissionSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  companyId: UUIDSchema,
  company: SubmissionCompanySchema.optional(),
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().nullable().optional(),
  submissionNumber: z.int(),
  status: EDI844SubmissionStatusSchema,
  deliveryMethod: EDI844DeliveryMethodSchema,
  totalClaimedAmount: DecimalStringSchema,
  totalAcceptedAmount: DecimalStringSchema.nullable().optional(),
  submissionDate: z.coerce.date(),
  sentAt: z.coerce.date().nullable().optional(),
  externalReference: z.string().nullable().optional(),
  outboundEmailId: UUIDSchema.nullable().optional(),
  vdmId: UUIDSchema.nullable().optional(),
  vdm: SubmissionVDMSchema.nullable().optional(),
  vdms: z.array(SubmissionVDMSchema).optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  lineCount: z.int().optional(),
  lines: z.array(EDI844SubmissionLineSchema).optional(),
  responseStatus: EDI844ResponseStatusSchema.optional(),
  linkedResponses: z.array(LinkedResponseSummarySchema).optional(),
  resubmissionEnabled: z.boolean().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

/**
 * List Submissions input schema with filters
 */
export const ListEDI844SubmissionsInputSchema = z.object({
  // Filter by status
  status: z.array(EDI844SubmissionStatusSchema).optional(),
  // Filter by company (single or multiple)
  companyId: UUIDSchema.optional(),
  companyIds: z.array(UUIDSchema).optional(),
  // Filter by division code (single or multiple)
  divisionCode: z.string().optional(),
  divisionCodes: z.array(z.string()).optional(),
  // Filter by selling location
  sellingLocationCode: z.string().optional(),
  // Filter by delivery method
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  // Filter by submission number
  submissionNumber: z.int().optional(),
  // Date filters
  submissionDateFrom: z.string().optional(),
  submissionDateTo: z.string().optional(),
  sentAtFrom: z.string().optional(),
  sentAtTo: z.string().optional(),
  // Batch number filter — each entry is an exact batch ("12345") or an
  // inclusive range ("12341-12349"). Entries are OR'd. Matches the
  // BatchNumberFilter component's output (comma-separated list / ranges).
  batchNumber: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  // Submission (Gently ID) number filter — exact ("123") or inclusive range
  // ("100-199"); entries are OR'd. Matches the BatchNumberFilter output.
  submissionNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  // VDM number filter — exact ("123") or inclusive range ("100-199"); entries
  // are OR'd. Matches COALESCE(vdm_number, gently_vdm_number) on linked VDMs.
  vdmNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  // Free-text search (ILIKE on submission number, company name)
  search: z.string().optional(),
  // Include relations
  includeLines: z.boolean().optional(),
  includeCompany: z.boolean().optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List Submissions response schema
 */
export const ListEDI844SubmissionsResponseSchema = z.object({
  submissions: z.array(EDI844SubmissionSchema),
  pagination: EDI844PaginationSchema,
});

/**
 * Get single Submission input schema
 */
export const GetEDI844SubmissionInputSchema = z.object({
  id: UUIDSchema,
  includeLines: z.boolean().optional(),
  includeCompany: z.boolean().optional(),
  includeLineSourceTransactions: z.boolean().optional(),
});

/**
 * Get Submission response schema
 */
export const GetEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Create Submission input schema
 */
export const CreateEDI844SubmissionInputSchema = z.object({
  companyId: UUIDSchema,
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().optional(),
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  submissionDate: z.coerce.date().optional(),
  externalReference: z.string().optional(),
  // IDs of RexelE844sd records to include in this submission
  transactionIds: z.array(UUIDSchema).min(1),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Create Submission response schema
 */
export const CreateEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Update Submission input schema
 */
export const UpdateEDI844SubmissionInputSchema = z.object({
  id: UUIDSchema,
  status: EDI844SubmissionStatusSchema.optional(),
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  externalReference: z.string().nullable().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  outboundEmailId: UUIDSchema.nullable().optional(),
  vdmId: UUIDSchema.nullable().optional(),
  totalAcceptedAmount: DecimalStringSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Submission response schema
 */
export const UpdateEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Update Submission Line input schema
 */
export const UpdateEDI844SubmissionLineInputSchema = z.object({
  id: UUIDSchema,
  lineStatus: EDI844SubmissionLineStatusSchema.optional(),
  acceptedAmount: DecimalStringSchema.nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Submission Line response schema
 */
export const UpdateEDI844SubmissionLineResponseSchema =
  EDI844SubmissionLineSchema;

/**
 * Batch Update Lines input schema (for processing 849 responses)
 */
export const BatchUpdateEDI844SubmissionLinesInputSchema = z.object({
  submissionId: UUIDSchema,
  lines: z.array(
    z.object({
      id: UUIDSchema,
      lineStatus: EDI844SubmissionLineStatusSchema,
      acceptedAmount: DecimalStringSchema.nullable().optional(),
      rejectionReason: z.string().nullable().optional(),
      responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
    }),
  ),
  // Optional: automatically calculate and update submission totals
  recalculateTotals: z.boolean().optional(),
});

/**
 * Batch Update Lines response schema
 */
export const BatchUpdateEDI844SubmissionLinesResponseSchema = z.object({
  submission: EDI844SubmissionSchema,
  updatedLines: z.array(EDI844SubmissionLineSchema),
});

/**
 * Inbound email summary for activity timeline
 */
export const ActivityInboundEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string(),
  from: z.string().nullable().optional(),
});

/**
 * EDI844 Submission Activity schema for RPC responses
 */
export const EDI844SubmissionActivitySchema = z.object({
  id: UUIDSchema,
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.nullable().optional(),
  activityType: EDI844SubmissionActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  inboundEmail: ActivityInboundEmailSchema.nullable().optional(),
  ediDocumentType: z.string().nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * List Submission Activities input schema
 */
export const ListEDI844SubmissionActivitiesInputSchema = z.object({
  submissionId: UUIDSchema,
  // Filter by activity type
  activityType: z.array(EDI844SubmissionActivityTypeSchema).optional(),
  // Filter by line (null = submission-level activities only)
  submissionLineId: UUIDSchema.nullable().optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List Submission Activities response schema
 */
export const ListEDI844SubmissionActivitiesResponseSchema = z.object({
  activities: z.array(EDI844SubmissionActivitySchema),
  pagination: EDI844PaginationSchema,
});

/**
 * Add Comment input schema
 */
export const AddEDI844SubmissionCommentInputSchema = z.object({
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.optional(),
  comment: z.string().min(1).max(2000),
});

/**
 * Add Comment response schema
 */
export const AddEDI844SubmissionCommentResponseSchema =
  EDI844SubmissionActivitySchema;

/**
 * Log Activity input schema (for system events)
 */
export const LogEDI844SubmissionActivityInputSchema = z.object({
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.optional(),
  activityType: EDI844SubmissionActivityTypeSchema,
  description: z.string(),
  inboundEmailId: UUIDSchema.optional(),
  ediDocumentType: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Log Activity response schema
 */
export const LogEDI844SubmissionActivityResponseSchema =
  EDI844SubmissionActivitySchema;

/**
 * Group pending transactions by batch key (supplier + division + selling location)
 */
export const GetEDI844BatchGroupsInputSchema = z.object({
  // Optional filters
  companyId: UUIDSchema.optional(),
  divisionCode: z.string().optional(),
  sellingLocationCode: z.string().optional(),
  // Only include transactions not yet in a submission
  excludeSubmitted: z.boolean().optional(),
});

/**
 * Batch group summary
 */
const BatchGroupSchema = z.object({
  companyId: UUIDSchema,
  companyName: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().nullable().optional(),
  transactionCount: z.int(),
  totalClaimAmount: z.number(),
  transactionIds: z.array(UUIDSchema),
});

/**
 * Get Batch Groups response schema
 */
export const GetEDI844BatchGroupsResponseSchema = z.object({
  groups: z.array(BatchGroupSchema),
});

/**
 * Send Workbench Transactions input schema
 */
const PreAssignedBatchSchema = z.object({
  companyCode: z.string().min(1),
  // Supplier discriminator — batches are keyed by company:supplier:division:duns.
  // Without it, two trading partners sharing a (company, division, DUNS) would
  // collide on one batch number. Compared case-insensitively on the backend.
  tradingPartner: z.string(),
  divisionCode: z.string().min(1),
  // DUNS/GL branch — each distinct DUNS location gets its own batch number.
  dunsGlBranch: z.string(),
  batchNumber: z.number().int().positive(),
});

export const SendWorkbenchTransactionsInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
  vdmIds: z.array(UUIDSchema).optional(),
  preAssignedBatches: z.array(PreAssignedBatchSchema).optional(),
});

/**
 * Submission info returned for each division batch
 */
const WorkbenchSubmissionInfoSchema = z.object({
  id: UUIDSchema,
  division: z.string(),
  batchNumber: z.string().nullable(),
  status: EDI844SubmissionStatusSchema,
  transactionCount: z.int(),
  totalClaimedAmount: z.string(),
});

/**
 * Failed transaction info
 */
const WorkbenchFailedTransactionSchema = z.object({
  id: UUIDSchema,
  reason: z.string(),
});

/**
 * VDM preview item — auto-populated from submitted transactions,
 * grouped by (vendorNo, company, division, taxProvince).
 */
const VDMPreviewItemSchema = z.object({
  supplierVendorNumber: z.string(),
  supplierName: z.string(),
  companyCode: z.string(),
  divisionCode: z.string(),
  branchCode: z.string(),
  calculateTax: z.boolean(),
  shippingProvince: z.string(),
  grossAmount: z.string(),
  emailCcAddress: z.string(),
  deliveryMethod: EDI844DeliveryMethodSchema,
  includeVdmNumber: z.boolean(),
  transactionCount: z.int(),
  transactionIds: z.array(UUIDSchema),
});

/**
 * The outcome of a batch send, stored on the background job's payload under
 * `result` once the Temporal run succeeds. The frontend parses it back out of
 * the polled job with this schema — it is no longer the RPC response itself
 * (large sends outlive the browser's ~300s fetch limit, so the procedure only
 * starts the job).
 */
export const Send844BatchResultSchema = z.object({
  successful: z.array(UUIDSchema),
  failed: z.array(WorkbenchFailedTransactionSchema),
  submissions: z.array(WorkbenchSubmissionInfoSchema),
  summary: z.object({
    totalRequested: z.int(),
    successCount: z.int(),
    failedCount: z.int(),
    submissionCount: z.int(),
  }),
});

/**
 * Send Workbench Transactions response schema — the send runs in the
 * background; poll `core.backgroundJobs.get` with this id until it settles.
 */
export const SendWorkbenchTransactionsResponseSchema = z.object({
  backgroundJobId: UUIDSchema,
});

/**
 * Preview Batch VDMs input schema — computes VDM groups without side effects
 */
export const PreviewBatchVDMsInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
});

const ExcludedTransactionSchema = z.object({
  id: UUIDSchema,
  reason: z.string(),
  claimAmount: z.number(),
});

export const PreviewBatchVDMsResponseSchema = z.object({
  items: z.array(VDMPreviewItemSchema),
  excluded: z.array(ExcludedTransactionSchema),
});

/**
 * Pre-assign batch numbers for VDM-first EDI divisions
 */
export const PreAssignBatchNumbersInputSchema = z.object({
  // The VDM-first EDI transaction ids. The backend derives the distinct
  // (company, division, duns_gl_branch) tuples from these rows and assigns one
  // batch number per tuple — batching is per DUNS location, not per division.
  transactionIds: z.array(UUIDSchema).min(1),
});

export const PreAssignBatchNumbersResponseSchema = z.object({
  batches: z.array(PreAssignedBatchSchema),
  // transactionId → the batch number reserved for that transaction's DUNS
  // location. Lets the VDM preview scope each VDM's SI2 batch list to the batch
  // numbers of its own transactions (a VDM is per tax province) instead of
  // listing every batch in the division.
  batchNumbersByTransactionId: z.record(
    z.string(),
    z.number().int().positive(),
  ),
});

/**
 * Send Test Email input schema — sends Excel without state changes
 */
export const SendTestEmailInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
  recipientEmail: z.string().email(),
});

export const SendTestEmailResponseSchema = z.object({
  success: z.boolean(),
  transactionCount: z.int(),
  recipientEmail: z.string(),
});

/**
 * Mark submissions as AS400 confirmed input schema
 */
export const MarkAsAS400ConfirmedInputSchema = z.object({
  submissionIds: z.array(UUIDSchema).min(1),
});

/**
 * Mark submissions as AS400 confirmed response schema
 */
export const MarkAsAS400ConfirmedResponseSchema = z.object({
  updatedCount: z.int(),
  submissionIds: z.array(UUIDSchema),
});

/**
 * Summary stats for the top cards on the 844 submissions page.
 * Independent of pagination so cards don't refresh when paging.
 */
export const GetEDI844SummaryStatsResponseSchema = z.object({
  totalSubmissions: z.number().int().nonnegative(),
  totalLines: z.number().int().nonnegative(),
  totalClaimedAmount: DecimalStringSchema,
});

/**
 * Supplier stats for charts on 844 submissions page
 */
export const GetEDI844SupplierStatsResponseSchema = z.object({
  suppliers: z.array(
    z.object({
      supplierId: z.string(),
      supplierName: z.string(),
      sent: z.number().int().nonnegative(),
      responded: z.number().int().nonnegative(),
      accepted: z.number().int().nonnegative(),
      rejected: z.number().int().nonnegative(),
      partial: z.number().int().nonnegative(),
    }),
  ),
});

/**
 * Link Submission to VDM input schema
 */
export const LinkSubmissionToVDMInputSchema = z.object({
  submissionId: UUIDSchema,
  vdmId: UUIDSchema,
});

export const LinkSubmissionToVDMResponseSchema = EDI844SubmissionSchema;

/**
 * Unlink Submission from VDM input schema
 */
export const UnlinkSubmissionFromVDMInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const UnlinkSubmissionFromVDMResponseSchema = EDI844SubmissionSchema;

/**
 * Auto-link VDMs to submissions after batch-and-send.
 * Matches by (companyCode, divisionCode, companyId/supplier).
 */
export const AutoLinkVdmsToSubmissionsInputSchema = z.object({
  vdmIds: z.array(UUIDSchema).min(1),
  submissionIds: z.array(UUIDSchema).min(1),
});

export const AutoLinkVdmsToSubmissionsResponseSchema = z.object({
  linked: z.array(
    z.object({
      submissionId: UUIDSchema,
      vdmId: UUIDSchema,
    }),
  ),
  unlinked: z.array(UUIDSchema),
});

/**
 * Mark a submission as failed (supplier rejected out-of-band).
 * Transitions sd_e844d rows to resubmission_pending so they can be edited.
 */
export const MarkSubmissionAsFailedInputSchema = z.object({
  submissionId: UUIDSchema,
  // Operator note (why the batch was cancelled) — leaves an audit trail on the
  // MARKED_FAILED activity + per-transaction activity. A reason is mandatory,
  // but the field is kept optional in the schema (non-breaking API-wise) and
  // enforced non-empty in the handler; the UI also blocks submit without it.
  comment: z.string().trim().max(2000).optional(),
});

export const MarkSubmissionAsFailedResponseSchema = EDI844SubmissionSchema;

/**
 * Resubmit a failed submission with a new batch number.
 * All line items are resubmitted together.
 */
export const ResubmitSubmissionInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const ResubmitSubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Download the original blob file (TSV uploaded to AS400) as an Excel with column headers.
 */
export const DownloadSubmissionBlobExcelInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const DownloadSubmissionBlobExcelResponseSchema = z.object({
  excel: z.string(),
  filename: z.string(),
});

/**
 * Download the filtered submission list as an Excel workbook — one row per
 * submission, mirroring the 844 Submissions table. Takes the same filters as
 * `list` (pagination excluded: the export covers every match, up to a cap).
 */
export const DownloadEDI844SubmissionsExcelInputSchema =
  ListEDI844SubmissionsInputSchema.omit({
    limit: true,
    offset: true,
    includeLines: true,
    includeCompany: true,
  });

export const DownloadEDI844SubmissionsExcelResponseSchema = z.object({
  /** base64-encoded .xlsx bytes */
  excel: z.string(),
  filename: z.string(),
  /** Rows written to the workbook. */
  count: z.int(),
  /** True when more submissions matched than the export cap allows. */
  truncated: z.boolean(),
});

/**
 * Get the parsed blob data (TSV rows + column headers) for inline preview.
 */
export const GetSubmissionBlobDataInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const GetSubmissionBlobDataResponseSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  blobKey: z.string(),
});

/**
 * Dev-only: simulate AS400 VDM response for a PENDING_VDM submission.
 */
export const DevSimulateVdmResponseInputSchema = z.object({
  submissionId: UUIDSchema,
  outcome: z.enum(["confirmed", "failed"]),
});

export const DevSimulateVdmResponseResponseSchema = z.object({
  success: z.boolean(),
  vdmNumber: z.int().optional(),
});

/**
 * EDI844 Submissions RPC Contracts
 */
export const edi844SubmissionsContracts = {
  // Submission CRUD
  list: oc
    .input(ListEDI844SubmissionsInputSchema)
    .output(ListEDI844SubmissionsResponseSchema)
    .route({
      method: "GET",
    }),
  get: oc
    .input(GetEDI844SubmissionInputSchema)
    .output(GetEDI844SubmissionResponseSchema)
    .route({
      method: "GET",
    }),
  create: oc
    .input(CreateEDI844SubmissionInputSchema)
    .output(CreateEDI844SubmissionResponseSchema)
    .route({
      method: "POST",
    }),
  update: oc
    .input(UpdateEDI844SubmissionInputSchema)
    .output(UpdateEDI844SubmissionResponseSchema)
    .route({
      method: "PATCH",
    }),

  // Line operations
  updateLine: oc
    .input(UpdateEDI844SubmissionLineInputSchema)
    .output(UpdateEDI844SubmissionLineResponseSchema)
    .route({
      method: "PATCH",
    }),
  batchUpdateLines: oc
    .input(BatchUpdateEDI844SubmissionLinesInputSchema)
    .output(BatchUpdateEDI844SubmissionLinesResponseSchema)
    .route({
      method: "POST",
    }),

  // Activity/timeline
  listActivities: oc
    .input(ListEDI844SubmissionActivitiesInputSchema)
    .output(ListEDI844SubmissionActivitiesResponseSchema)
    .route({
      method: "GET",
    }),
  addComment: oc
    .input(AddEDI844SubmissionCommentInputSchema)
    .output(AddEDI844SubmissionCommentResponseSchema)
    .route({
      method: "POST",
    }),
  logActivity: oc
    .input(LogEDI844SubmissionActivityInputSchema)
    .output(LogEDI844SubmissionActivityResponseSchema)
    .route({
      method: "POST",
    }),

  // Batch grouping helper
  getBatchGroups: oc
    .input(GetEDI844BatchGroupsInputSchema)
    .output(GetEDI844BatchGroupsResponseSchema)
    .route({
      method: "GET",
    }),

  // VDM preview (read-only, no side effects)
  previewBatchVDMs: oc
    .input(PreviewBatchVDMsInputSchema)
    .output(PreviewBatchVDMsResponseSchema)
    .route({
      method: "POST",
    }),

  // Pre-assign batch numbers for VDM-first EDI divisions
  preAssignBatchNumbers: oc
    .input(PreAssignBatchNumbersInputSchema)
    .output(PreAssignBatchNumbersResponseSchema)
    .route({
      method: "POST",
    }),

  // Workbench send
  sendWorkbenchTransactions: oc
    .input(SendWorkbenchTransactionsInputSchema)
    .output(SendWorkbenchTransactionsResponseSchema)
    .route({
      method: "POST",
    }),

  // Test email (no state changes)
  sendTestEmail: oc
    .input(SendTestEmailInputSchema)
    .output(SendTestEmailResponseSchema)
    .route({
      method: "POST",
    }),

  // Mark submissions as AS400 confirmed
  markAsAS400Confirmed: oc
    .input(MarkAsAS400ConfirmedInputSchema)
    .output(MarkAsAS400ConfirmedResponseSchema)
    .route({
      method: "POST",
    }),

  // Summary stats for top cards (independent of pagination)
  getSummaryStats: oc.output(GetEDI844SummaryStatsResponseSchema).route({
    method: "GET",
  }),

  // Supplier stats for charts
  getSupplierStats: oc.output(GetEDI844SupplierStatsResponseSchema).route({
    method: "GET",
  }),

  // VDM linking
  linkVdm: oc
    .input(LinkSubmissionToVDMInputSchema)
    .output(LinkSubmissionToVDMResponseSchema)
    .route({
      method: "POST",
    }),
  unlinkVdm: oc
    .input(UnlinkSubmissionFromVDMInputSchema)
    .output(UnlinkSubmissionFromVDMResponseSchema)
    .route({
      method: "POST",
    }),

  autoLinkVdmsToSubmissions: oc
    .input(AutoLinkVdmsToSubmissionsInputSchema)
    .output(AutoLinkVdmsToSubmissionsResponseSchema)
    .route({
      method: "POST",
    }),

  // Mark submission as failed (supplier rejected out-of-band)
  markAsFailed: oc
    .input(MarkSubmissionAsFailedInputSchema)
    .output(MarkSubmissionAsFailedResponseSchema)
    .route({
      method: "POST",
    }),

  // Resubmit a failed submission with new batch number
  resubmit: oc
    .input(ResubmitSubmissionInputSchema)
    .output(ResubmitSubmissionResponseSchema)
    .route({
      method: "POST",
    }),

  downloadBlobExcel: oc
    .input(DownloadSubmissionBlobExcelInputSchema)
    .output(DownloadSubmissionBlobExcelResponseSchema)
    .route({
      method: "POST",
    }),

  downloadExcel: oc
    .input(DownloadEDI844SubmissionsExcelInputSchema)
    .output(DownloadEDI844SubmissionsExcelResponseSchema)
    .route({
      method: "POST",
    }),

  getBlobData: oc
    .input(GetSubmissionBlobDataInputSchema)
    .output(GetSubmissionBlobDataResponseSchema)
    .route({
      method: "GET",
    }),

  devSimulateVdmResponse: oc
    .input(DevSimulateVdmResponseInputSchema)
    .output(DevSimulateVdmResponseResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

// Type exports
export type EDI844SubmissionStatus = z.infer<
  typeof EDI844SubmissionStatusSchema
>;
export type EDI844DeliveryMethod = z.infer<typeof EDI844DeliveryMethodSchema>;
export type EDI844SubmissionLineStatus = z.infer<
  typeof EDI844SubmissionLineStatusSchema
>;
export type EDI844SubmissionActivityType = z.infer<
  typeof EDI844SubmissionActivityTypeSchema
>;
export type EDI844SubmissionLine = z.infer<typeof EDI844SubmissionLineSchema>;
export type EDI844Submission = z.infer<typeof EDI844SubmissionSchema>;
export type ListEDI844SubmissionsInput = z.infer<
  typeof ListEDI844SubmissionsInputSchema
>;
export type ListEDI844SubmissionsResponse = z.infer<
  typeof ListEDI844SubmissionsResponseSchema
>;
export type GetEDI844SubmissionInput = z.infer<
  typeof GetEDI844SubmissionInputSchema
>;
export type GetEDI844SubmissionResponse = z.infer<
  typeof GetEDI844SubmissionResponseSchema
>;
export type CreateEDI844SubmissionInput = z.infer<
  typeof CreateEDI844SubmissionInputSchema
>;
export type CreateEDI844SubmissionResponse = z.infer<
  typeof CreateEDI844SubmissionResponseSchema
>;
export type UpdateEDI844SubmissionInput = z.infer<
  typeof UpdateEDI844SubmissionInputSchema
>;
export type UpdateEDI844SubmissionResponse = z.infer<
  typeof UpdateEDI844SubmissionResponseSchema
>;
export type UpdateEDI844SubmissionLineInput = z.infer<
  typeof UpdateEDI844SubmissionLineInputSchema
>;
export type UpdateEDI844SubmissionLineResponse = z.infer<
  typeof UpdateEDI844SubmissionLineResponseSchema
>;
export type BatchUpdateEDI844SubmissionLinesInput = z.infer<
  typeof BatchUpdateEDI844SubmissionLinesInputSchema
>;
export type BatchUpdateEDI844SubmissionLinesResponse = z.infer<
  typeof BatchUpdateEDI844SubmissionLinesResponseSchema
>;
export type EDI844SubmissionActivity = z.infer<
  typeof EDI844SubmissionActivitySchema
>;
export type ListEDI844SubmissionActivitiesInput = z.infer<
  typeof ListEDI844SubmissionActivitiesInputSchema
>;
export type ListEDI844SubmissionActivitiesResponse = z.infer<
  typeof ListEDI844SubmissionActivitiesResponseSchema
>;
export type AddEDI844SubmissionCommentInput = z.infer<
  typeof AddEDI844SubmissionCommentInputSchema
>;
export type AddEDI844SubmissionCommentResponse = z.infer<
  typeof AddEDI844SubmissionCommentResponseSchema
>;
export type LogEDI844SubmissionActivityInput = z.infer<
  typeof LogEDI844SubmissionActivityInputSchema
>;
export type LogEDI844SubmissionActivityResponse = z.infer<
  typeof LogEDI844SubmissionActivityResponseSchema
>;
export type GetEDI844BatchGroupsInput = z.infer<
  typeof GetEDI844BatchGroupsInputSchema
>;
export type GetEDI844BatchGroupsResponse = z.infer<
  typeof GetEDI844BatchGroupsResponseSchema
>;
export type SendWorkbenchTransactionsInput = z.infer<
  typeof SendWorkbenchTransactionsInputSchema
>;
export type SendWorkbenchTransactionsResponse = z.infer<
  typeof SendWorkbenchTransactionsResponseSchema
>;
export type Send844BatchResult = z.infer<typeof Send844BatchResultSchema>;
export type PreviewBatchVDMsInput = z.infer<typeof PreviewBatchVDMsInputSchema>;
export type PreviewBatchVDMsResponse = z.infer<
  typeof PreviewBatchVDMsResponseSchema
>;
export type SendTestEmailInput = z.infer<typeof SendTestEmailInputSchema>;
export type SendTestEmailResponse = z.infer<typeof SendTestEmailResponseSchema>;
export type MarkAsAS400ConfirmedInput = z.infer<
  typeof MarkAsAS400ConfirmedInputSchema
>;
export type MarkAsAS400ConfirmedResponse = z.infer<
  typeof MarkAsAS400ConfirmedResponseSchema
>;
export type GetEDI844SummaryStatsResponse = z.infer<
  typeof GetEDI844SummaryStatsResponseSchema
>;
export type GetEDI844SupplierStatsResponse = z.infer<
  typeof GetEDI844SupplierStatsResponseSchema
>;
export type LinkSubmissionToVDMInput = z.infer<
  typeof LinkSubmissionToVDMInputSchema
>;
export type UnlinkSubmissionFromVDMInput = z.infer<
  typeof UnlinkSubmissionFromVDMInputSchema
>;
export type DevSimulateVdmResponseInput = z.infer<
  typeof DevSimulateVdmResponseInputSchema
>;
export type MarkSubmissionAsFailedInput = z.infer<
  typeof MarkSubmissionAsFailedInputSchema
>;
export type ResubmitSubmissionInput = z.infer<
  typeof ResubmitSubmissionInputSchema
>;
export type DownloadSubmissionBlobExcelInput = z.infer<
  typeof DownloadSubmissionBlobExcelInputSchema
>;
export type DownloadEDI844SubmissionsExcelInput = z.infer<
  typeof DownloadEDI844SubmissionsExcelInputSchema
>;
export type PreAssignBatchNumbersInput = z.infer<
  typeof PreAssignBatchNumbersInputSchema
>;
export type PreAssignBatchNumbersResponse = z.infer<
  typeof PreAssignBatchNumbersResponseSchema
>;
export type PreAssignedBatch = z.infer<typeof PreAssignedBatchSchema>;
